import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import AuthContext from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import Base from '../components/Base';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Store, Package, TrendingUp, Plus, Eye, Copy, Edit, Trash2,
  ExternalLink, Sparkles, CheckCircle, AlertCircle, Zap, ShoppingBag, Clock
} from 'lucide-react';
import COLORS from '../utils/colors';
import BrandOnboarding from '../components/BrandOnboarding';
import API_BASE_URL from '../config/api';

const API_BASE_URL_FOR_IMAGES = API_BASE_URL;

const HomePage = () => {
  const navigate = useNavigate();
  const { authToken, logoutUser, user } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    is_active: false,
    expiry_date: null,
  });
  const [slideIndexes, setSlideIndexes] = useState({});
  const [showOnboarding, setShowOnboarding] = useState(false);

  const authAxios = useMemo(() => axios.create({
    baseURL: `${API_BASE_URL}/api`,
    headers: {
      Authorization: `Bearer ${authToken?.access}`,
    },
  }), [authToken?.access]);

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const response = await authAxios.get('/get_subscription_status/');
      setSubscriptionStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch subscription status:', err);
    }
  }, [authAxios]);

  const fetchProducts = useCallback(async () => {
    if (!authToken?.access) {
      navigate('/login');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await authAxios.get('/products/');

      if (response.data && Array.isArray(response.data.products)) {
        setProducts(response.data.products);
        const initialSlideIndexes = {};
        response.data.products.forEach((product, idx) => {
          initialSlideIndexes[idx] = 1;
        });
        setSlideIndexes(initialSlideIndexes);
      } else {
        setProducts([]);
      }
    } catch (err) {
      console.error('Error fetching products:', err.response || err);
      setError('Failed to load products. Please try again later.');
      if (err.response?.status === 401) {
        Swal.fire({
          title: 'Session Expired',
          text: 'Please login again',
          icon: 'warning',
        }).then(() => {
          logoutUser();
          navigate('/login');
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [authAxios, authToken?.access, navigate, logoutUser]);

  useEffect(() => {
    if (authToken) {
      fetchSubscriptionStatus();
      fetchProducts();

      const onboardingCompleted = localStorage.getItem('onboarding_completed');
      if (!onboardingCompleted) {
        setTimeout(() => {
          setShowOnboarding(true);
        }, 500);
      }
    } else {
      navigate('/login');
    }
  }, [authToken, navigate, fetchSubscriptionStatus, fetchProducts]);

  const toggleStock = async (productId) => {
    if (!subscriptionStatus.is_active) {
      Swal.fire('Error!', 'Please subscribe to perform this action.', 'error');
      return;
    }
    try {
      const response = await authAxios.post(`/toggle_stock/${productId}/`);
      if (response.data.success) {
        setProducts((prevProducts) =>
          prevProducts.map((product) =>
            product.id === productId
              ? { ...product, in_stock: response.data.in_stock }
              : product
          )
        );
        Swal.fire(
          'Success!',
          `Product is now ${response.data.in_stock ? 'In Stock' : 'Out of Stock'}.`,
          'success'
        );
      } else {
        Swal.fire('Error!', 'Failed to toggle stock status.', 'error');
      }
    } catch (err) {
      console.error('Error toggling stock:', err.response || err);
      Swal.fire('Error!', 'An error occurred while toggling stock status.', 'error');
    }
  };

  const confirmDelete = (productId) => {
    if (!subscriptionStatus.is_active) {
      Swal.fire('Error!', 'Please subscribe to perform this action.', 'error');
      return;
    }
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await authAxios.delete(`/delete_product/${productId}/`);
          Swal.fire('Deleted!', 'The product has been deleted.', 'success');
          setProducts((prevProducts) =>
            prevProducts.filter((product) => product.id !== productId)
          );
        } catch (err) {
          console.error('Error deleting product:', err.response || err);
          Swal.fire('Error!', 'Failed to delete the product.', 'error');
        }
      }
    });
  };

  const handleCopyUrl = (url) => {
    if (!subscriptionStatus.is_active) {
      Swal.fire('Error!', 'Please subscribe to perform this action.', 'error');
      return;
    }
    navigator.clipboard.writeText(url).then(() => {
      const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      Toast.fire({
        icon: "success",
        title: "Product URL copied to clipboard!"
      });
    }).catch(err => {
      console.error('Failed to copy URL:', err);
      Swal.fire('Error', 'Failed to copy URL. Please try manually.', 'error');
    });
  };

  const handleEdit = (productId) => {
    if (!subscriptionStatus.is_active) {
      Swal.fire('Error!', 'Please subscribe to perform this action.', 'error');
      return;
    }
    navigate(`/EditProduct/${productId}`);
  };

  const handleUploadProduct = (e) => {
    if (!subscriptionStatus.is_active) {
      e.preventDefault();
      Swal.fire('Error!', 'Please subscribe to perform this action.', 'error');
    }
  };

  const plusSlides = (n, productIndex) => {
    setSlideIndexes((prev) => {
      const newIndexes = { ...prev };
      const currentProductSlideIndex = newIndexes[productIndex] || 1;
      const slides = document.querySelectorAll(`#slideshow-container-${productIndex} .mySlides`);

      if (!slides || slides.length === 0) return newIndexes;

      let nextSlideIndex = currentProductSlideIndex + n;

      if (nextSlideIndex > slides.length) nextSlideIndex = 1;
      if (nextSlideIndex < 1) nextSlideIndex = slides.length;

      newIndexes[productIndex] = nextSlideIndex;
      return newIndexes;
    });
  };

  const currentSlide = (n, productIndex) => {
    setSlideIndexes((prev) => ({
      ...prev,
      [productIndex]: n,
    }));
  };

  const showSlides = useCallback((productIndex) => {
    const slides = document.querySelectorAll(`#slideshow-container-${productIndex} .mySlides`);
    const dots = document.querySelectorAll(`#slideshow-container-${productIndex} .dot`);

    if (!slides || slides.length === 0) return;

    slides.forEach((slide) => (slide.style.display = 'none'));
    dots.forEach((dot) => {
      dot.classList.remove('bg-orange-500');
      dot.classList.add('bg-gray-300');
    });

    const currentProductSlideIndex = slideIndexes[productIndex] || 1;
    if (slides[currentProductSlideIndex - 1]) {
      slides[currentProductSlideIndex - 1].style.display = 'block';
    }
    if (dots.length > 0 && dots[currentProductSlideIndex - 1]) {
      dots[currentProductSlideIndex - 1].classList.remove('bg-gray-300');
      dots[currentProductSlideIndex - 1].classList.add('bg-orange-500');
    }
  }, [slideIndexes]);

  useEffect(() => {
    products.forEach((_, index) => {
      if (slideIndexes[index] !== undefined) {
        showSlides(index);
      }
    });
  }, [products, slideIndexes, showSlides]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.4, ease: "easeOut" }
    }
  };

  if (isLoading) {
    return (
      <Base Content={
        <div className="flex flex-col justify-center items-center h-screen bg-white">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 360],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="w-16 h-16 border-4 border-t-transparent rounded-full mb-4"
            style={{ borderColor: COLORS.AUTUMN }}
          />
          <p className="text-xl text-gray-700 font-medium">Loading your brand dashboard...</p>
        </div>
      } />
    );
  }

  if (error) {
    return (
      <Base Content={
        <div className="flex justify-center items-center h-screen bg-white">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-xl text-red-500">{error}</p>
          </div>
        </div>
      } />
    );
  }

  return (
    <>
      <BrandOnboarding
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        username={user?.username || 'yourstore'}
      />
      <Base
        Content={
          <div className="min-h-screen px-3 sm:px-4 lg:px-6 py-6 sm:py-8 max-w-7xl mx-auto bg-white">
            {/* CLEAN BRAND HEADER - WHITE VERSION */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-6 sm:mb-8 p-4 sm:p-6 lg:p-8 rounded-2xl sm:rounded-3xl shadow-xl border-2 border-gray-200 bg-white overflow-hidden relative"
            >
              <div className="relative flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 lg:gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <Store className="w-6 h-6 sm:w-8 sm:h-8 text-gray-900" />
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Your Fashion Brand</h1>
                  </div>
                  <p className="text-base sm:text-lg text-gray-600">Manage your independent online store</p>
                </div>

                {/* Store URL Card */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="w-full lg:w-auto bg-white rounded-xl sm:rounded-2xl p-4 sm:p-5 border-2 border-gray-200 shadow-lg"
                >
                  <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 mb-2">
                    <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: COLORS.AUTUMN }} />
                    <span>Your Store is Live</span>
                  </div>
                  <a
                    href={user?.username ? `https://${user.username}.vasstr.com` : '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-base sm:text-lg lg:text-xl font-bold hover:underline flex items-center gap-2 break-all"
                    style={{ color: COLORS.AUTUMN }}
                  >
                    {user?.username ? `${user.username}.vasstr.com` : 'yourstore.vasstr.com'}
                    <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  </a>
                </motion.div>
              </div>
            </motion.div>

            {/* CLEAN STATS CARDS */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8"
            >
              <motion.div
                variants={itemVariants}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="group bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-lg p-5 sm:p-6 border-2 border-gray-200 transition-all duration-300"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2 font-medium">Total Designs</div>
                    <div className="text-3xl sm:text-4xl font-bold mb-1 text-gray-900">{products.length}</div>
                    <div className="text-xs text-gray-500">in your catalog</div>
                  </div>
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-white border-2 border-gray-200 flex items-center justify-center group-hover:border-gray-300 transition-colors">
                    <Package className="w-6 h-6 sm:w-7 sm:h-7 text-gray-700" />
                  </div>
                </div>
              </motion.div>

              <motion.div
                variants={itemVariants}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="group bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-lg p-5 sm:p-6 border-2 border-gray-200 transition-all duration-300"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2 font-medium">Store Status</div>
                    <div className={`text-xl sm:text-2xl font-bold mb-1 ${subscriptionStatus.is_active ? 'text-green-600' : 'text-red-600'}`}>
                      {subscriptionStatus.is_active ? 'Active' : 'Inactive'}
                    </div>
                    <div className="text-xs text-gray-500">{subscriptionStatus.is_active ? 'Ready to sell' : 'Subscribe to activate'}</div>
                  </div>
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center transition-colors ${subscriptionStatus.is_active ? 'bg-green-100 group-hover:bg-green-200' : 'bg-red-100 group-hover:bg-red-200'}`}>
                    {subscriptionStatus.is_active ? <CheckCircle className="w-6 h-6 sm:w-7 sm:h-7 text-green-600" /> : <Clock className="w-6 h-6 sm:w-7 sm:h-7 text-red-600" />}
                  </div>
                </div>
              </motion.div>

              <motion.div
                variants={itemVariants}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="group bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-lg p-5 sm:p-6 border-2 border-gray-200 transition-all duration-300 sm:col-span-2 lg:col-span-1"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2 font-medium">In Stock</div>
                    <div className="text-3xl sm:text-4xl font-bold mb-1 text-gray-900">
                      {products.filter(p => p.in_stock).length}
                    </div>
                    <div className="text-xs text-gray-500">available now</div>
                  </div>
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-white border-2 border-gray-200 flex items-center justify-center group-hover:border-gray-300 transition-colors">
                    <ShoppingBag className="w-6 h-6 sm:w-7 sm:h-7 text-gray-700" />
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* SUBSCRIPTION WARNING */}
            {!subscriptionStatus.is_active && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border-2 border-red-200 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8 shadow-md"
              >
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-red-900 mb-1 text-base sm:text-lg">Subscription Expired</p>
                      <p className="text-sm text-red-700">Subscribe now to manage your store and start selling!</p>
                    </div>
                  </div>
                  <Link to="/subscription" className="w-full sm:w-auto">
                    <button className="w-full sm:w-auto text-white font-semibold px-6 py-3 rounded-lg transition hover:shadow-lg text-sm sm:text-base" style={{ backgroundColor: COLORS.AUTUMN }}>
                      Subscribe Now
                    </button>
                  </Link>
                </div>
              </motion.div>
            )}

            {/* GETTING STARTED CHECKLIST - PURE WHITE VERSION */}
            {products.length < 3 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white border-2 border-gray-200 rounded-xl sm:rounded-2xl p-5 sm:p-6 mb-6 sm:mb-8 shadow-md"
              >
                <div className="flex flex-col sm:flex-row items-start justify-between gap-3 sm:gap-4 mb-5 sm:mb-6">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gray-700 text-white flex items-center justify-center flex-shrink-0 shadow-md">
                      <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6" />
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-1">Getting Started Checklist</h3>
                      <p className="text-xs sm:text-sm text-gray-600">Complete these steps to set up your brand!</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      localStorage.removeItem('onboarding_completed');
                      setShowOnboarding(true);
                    }}
                    className="w-full sm:w-auto bg-white border-2 border-gray-300 text-gray-700 font-medium px-4 py-2 rounded-lg text-xs sm:text-sm whitespace-nowrap transition hover:bg-white hover:border-gray-400 hover:shadow-md flex items-center justify-center gap-2"
                  >
                    <Sparkles className="w-4 h-4" />
                    View Tutorial
                  </button>
                </div>

                <div className="space-y-3 sm:space-y-4">
                  <div className={`flex items-start gap-3 p-3 sm:p-4 rounded-xl transition-all duration-300 ${products.length > 0 ? 'bg-green-50 border-2 border-green-300 shadow-sm' : 'bg-white border-2 border-gray-200 hover:border-gray-300'}`}>
                    <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${products.length > 0 ? 'bg-green-500' : 'bg-gray-300'}`}>
                      {products.length > 0 ? (
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      ) : (
                        <span className="text-white text-sm font-bold">1</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-gray-800 text-sm sm:text-base">Add your first design</p>
                      <p className="text-xs sm:text-sm text-gray-600 mb-2">Upload at least one product to your store</p>
                      {/* {products.length === 0 && (
                        // <Link to="/UploadProduct">
                        //   <button className="text-white font-medium px-4 py-2 rounded-lg text-xs sm:text-sm transition hover:shadow-lg hover:scale-105" style={{ backgroundColor: COLORS.AUTUMN }}>
                        //     Add Design Now
                        //   </button>
                        // </Link>
                      )} */}
                    </div>
                  </div>

                  <div className={`flex items-start gap-3 p-3 sm:p-4 rounded-xl transition-all duration-300 ${products.length >= 5 ? 'bg-green-50 border-2 border-green-300 shadow-sm' : 'bg-white border-2 border-gray-200'}`}>
                    <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${products.length >= 5 ? 'bg-green-500' : 'bg-gray-300'}`}>
                      {products.length >= 5 ? (
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      ) : (
                        <span className="text-white text-sm font-bold">2</span>
                      )}
                    </div>
                    <div>
                      <p className="font-bold text-gray-800 text-sm sm:text-base">Build your catalog ({products.length}/5+ designs)</p>
                      <p className="text-xs sm:text-sm text-gray-600">Add more designs for better customer selection</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 sm:p-4 bg-white border-2 border-gray-200 rounded-xl">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-bold">3</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-gray-800 text-sm sm:text-base mb-1">Share your store URL</p>
                      <p className="text-xs sm:text-sm text-gray-600 mb-3">Start promoting your brand</p>
                      <div className="flex flex-wrap gap-2">
                        <a href={user?.username ? `https://${user.username}.vasstr.com` : '#'} target="_blank" rel="noopener noreferrer">
                          <button className="bg-white border-2 font-medium px-4 py-2 rounded-lg text-xs sm:text-sm transition hover:shadow-md flex items-center gap-2" style={{ borderColor: COLORS.AUTUMN, color: COLORS.AUTUMN }}>
                            <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4" />
                            View Store
                          </button>
                        </a>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`https://${user?.username}.vasstr.com`);
                            Swal.fire({ icon: 'success', title: 'Copied!', text: 'Store URL copied', timer: 2000, showConfirmButton: false });
                          }}
                          className="bg-white border-2 border-gray-300 font-medium px-4 py-2 rounded-lg text-xs sm:text-sm transition hover:border-gray-400 text-gray-700 flex items-center gap-2"
                        >
                          <Copy className="w-3 h-3 sm:w-4 sm:h-4" />
                          Copy URL
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 sm:p-4 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-300 transition-all duration-300">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-bold">4</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-gray-800 text-sm sm:text-base">Complete Designer KYC</p>
                      <p className="text-xs sm:text-sm text-gray-600 mb-2">Verify your account to receive payouts</p>
                      <Link to="/designer-onboarding">
                        <button className="text-white font-medium px-4 py-2 rounded-lg text-xs sm:text-sm transition hover:shadow-lg hover:scale-105" style={{ backgroundColor: COLORS.AUTUMN }}>
                          Complete KYC
                        </button>
                      </Link>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 sm:p-4 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-300 transition-all duration-300">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-bold">5</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-gray-800 text-sm sm:text-base">Choose Your Template</p>
                      <p className="text-xs sm:text-sm text-gray-600 mb-2">Select and customize your store design from 20+ templates</p>
                      <Link to="/TemplateThemes">
                        <button className="text-white font-medium px-4 py-2 rounded-lg text-xs sm:text-sm transition hover:shadow-lg hover:scale-105" style={{ backgroundColor: COLORS.AUTUMN }}>
                          Browse theams
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* QUICK ACTIONS BANNER - PURE WHITE VERSION */}
            {products.length >= 3 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white border-2 border-gray-200 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8 shadow-md"
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <Zap className="w-6 h-6 text-gray-700 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-gray-800 mb-1 text-base sm:text-lg">Quick Actions</h3>
                      <p className="text-xs sm:text-sm text-gray-600">Manage your catalog and grow your brand</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    <Link to="/UploadProduct">
                      <button className="text-white font-medium px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm transition hover:shadow-lg flex items-center gap-2" style={{ backgroundColor: COLORS.AUTUMN }}>
                        <Plus className="w-4 h-4" />
                        Add Design
                      </button>
                    </Link>
                    <a href={user?.username ? `https://${user.username}.vasstr.com` : '#'} target="_blank" rel="noopener noreferrer">
                      <button className="bg-white border-2 font-medium px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm transition hover:shadow-md flex items-center gap-2" style={{ borderColor: COLORS.AUTUMN, color: COLORS.AUTUMN }}>
                        <Eye className="w-4 h-4" />
                        View Store
                      </button>
                    </a>
                    <button
                      onClick={() => {
                        localStorage.removeItem('onboarding_completed');
                        setShowOnboarding(true);
                      }}
                      className="bg-white border-2 border-gray-300 text-gray-700 font-medium px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm transition hover:border-gray-400 flex items-center gap-2"
                    >
                      <Sparkles className="w-4 h-4" />
                      Tutorial
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* CATALOG HEADER */}
            <div className="flex items-center justify-between mb-5 sm:mb-6">
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold flex items-center gap-2 sm:gap-3 text-gray-900">
                <Package className="w-6 h-6 sm:w-8 sm:h-8" style={{ color: COLORS.AUTUMN }} />
                Your Store Catalog
              </h2>
            </div>

            {/* PRODUCTS GRID */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-6 mb-10"
            >
              {/* ADD NEW DESIGN CARD - PURE WHITE VERSION */}
              <motion.div variants={itemVariants}>
                <Link
                  to="/UploadProduct"
                  onClick={handleUploadProduct}
                  className={`block ${!subscriptionStatus.is_active ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <motion.div
                    whileHover={subscriptionStatus.is_active ? { scale: 1.02, y: -5 } : {}}
                    className="flex flex-col items-center justify-center gap-3 p-6 sm:p-8 rounded-xl sm:rounded-2xl border-3 border-dashed h-[400px] sm:h-[460px] transition-all duration-300 bg-white shadow-md"
                    style={{ borderColor: COLORS.AUTUMN }}
                  >
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center shadow-lg" style={{ backgroundColor: COLORS.AUTUMN }}>
                      <Plus className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                    </div>
                    <p className="text-base sm:text-lg font-bold text-center" style={{ color: COLORS.AUTUMN }}>Add New Design</p>
                    <p className="text-xs sm:text-sm text-gray-600 text-center">Upload your latest creation</p>
                  </motion.div>
                </Link>
              </motion.div>

              {/* PRODUCT CARDS */}
              {products.map((product, index) => {
                const imageUrls = [product.img1, product.img2, product.img3, product.img4, product.img5, product.img6]
                  .filter(Boolean)
                  .map(imgUrl => {
                    if (imgUrl.startsWith('http://') || imgUrl.startsWith('https://') || imgUrl.startsWith('/media/')) {
                      return imgUrl;
                    }
                    return `${API_BASE_URL_FOR_IMAGES}${imgUrl}`;
                  });

                return (
                  <motion.div
                    key={product.id}
                    variants={itemVariants}
                    whileHover={{ y: -8, transition: { duration: 0.3 } }}
                    className="group bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-lg p-3 sm:p-4 h-[400px] sm:h-[460px] flex flex-col transition-all duration-300 border-2 border-gray-200"
                  >
                    {/* Image Slider */}
                    <div
                      id={`slideshow-container-${index}`}
                      className="relative w-full h-[55%] sm:h-[60%] overflow-hidden rounded-lg sm:rounded-xl mb-3"
                    >
                      {imageUrls.length > 1 && (
                        <>
                          <button
                            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-1.5 sm:p-2 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white shadow-lg transition-all duration-200 hover:scale-110"
                            onClick={() => plusSlides(-1, index)}
                            style={{ color: COLORS.AUTUMN }}
                          >
                            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 18l-6-6 6-6" />
                            </svg>
                          </button>
                          <button
                            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-1.5 sm:p-2 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white shadow-lg transition-all duration-200 hover:scale-110"
                            onClick={() => plusSlides(1, index)}
                            style={{ color: COLORS.AUTUMN }}
                          >
                            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 18l6-6-6-6" />
                            </svg>
                          </button>
                        </>
                      )}

                      {imageUrls.length > 0 ? (
                        imageUrls.map((img, idx) => (
                          <div key={idx} className="mySlides hidden w-full h-full">
                            <img
                              src={img}
                              alt={`Product ${idx + 1}`}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/400x400/EEEEEE/333333?text=Error'; }}
                            />
                          </div>
                        ))
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">No Image</div>
                      )}

                      {imageUrls.length > 1 && (
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1.5 z-10">
                          {imageUrls.map((_, i) => (
                            <button
                              key={i}
                              className={`dot w-2 h-2 rounded-full transition-all duration-300 ${(slideIndexes[index] || 1) === i + 1 ? 'w-6' : 'bg-gray-300'}`}
                              style={(slideIndexes[index] || 1) === i + 1 ? { backgroundColor: COLORS.AUTUMN } : {}}
                              onClick={() => currentSlide(i + 1, index)}
                            ></button>
                          ))}
                        </div>
                      )}

                      {!product.in_stock && (
                        <div className="absolute top-2 right-2 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                          Out of Stock
                        </div>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="flex-1 flex flex-col">
                      <div className="text-center mb-3">
                        <p className="font-bold text-sm sm:text-base truncate mb-1" style={{ color: COLORS.AUTUMN }} title={product.name}>
                          {product.name}
                        </p>
                        <p className="text-base sm:text-lg font-semibold text-gray-800">₹{parseFloat(product.price).toLocaleString('en-IN')}</p>
                        {product.Discountedprice && (
                          <p className="text-xs sm:text-sm text-green-600 font-medium">
                            Sale: ₹{parseFloat(product.Discountedprice).toLocaleString('en-IN')}
                          </p>
                        )}
                      </div>

                      {/* Action Icons */}
                      <div className="flex justify-center items-center gap-3 sm:gap-4 mb-3">
                        <button
                          onClick={() => handleCopyUrl(product.external_url)}
                          disabled={!subscriptionStatus.is_active}
                          className={`p-2 rounded-lg transition-all ${subscriptionStatus.is_active ? 'hover:bg-gray-100 hover:scale-110' : 'opacity-50 cursor-not-allowed'}`}
                          style={{ color: COLORS.AUTUMN }}
                          title="Copy URL"
                        >
                          <Copy className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        <a
                          href={product.external_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`p-2 rounded-lg transition-all ${subscriptionStatus.is_active ? 'hover:bg-gray-100 hover:scale-110' : 'opacity-50 cursor-not-allowed pointer-events-none'}`}
                          style={{ color: COLORS.AUTUMN }}
                          title="Preview"
                        >
                          <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                        </a>
                      </div>

                      {/* Action Buttons */}
                      <div className="grid grid-cols-2 gap-2 mt-auto">
                        <button
                          onClick={() => toggleStock(product.id)}
                          disabled={!subscriptionStatus.is_active}
                          className={`col-span-2 rounded-lg text-white text-xs sm:text-sm py-2 font-medium transition-all ${
                            subscriptionStatus.is_active ? 'hover:shadow-md hover:scale-102' : 'opacity-50 cursor-not-allowed'
                          }`}
                          style={{ backgroundColor: product.in_stock ? COLORS.AUTUMN : '#6b7280' }}
                        >
                          {product.in_stock ? 'In Stock' : 'Out of Stock'}
                        </button>
                        <button
                          onClick={() => handleEdit(product.id)}
                          disabled={!subscriptionStatus.is_active}
                          className={`border-2 rounded-lg text-xs sm:text-sm py-2 font-medium transition-all flex items-center justify-center gap-1 ${
                            subscriptionStatus.is_active ? 'hover:bg-gray-50' : 'opacity-50 cursor-not-allowed'
                          }`}
                          style={{ borderColor: COLORS.AUTUMN, color: COLORS.AUTUMN }}
                        >
                          <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => confirmDelete(product.id)}
                          disabled={!subscriptionStatus.is_active}
                          className={`border-2 rounded-lg text-xs sm:text-sm py-2 font-medium transition-all flex items-center justify-center gap-1 ${
                            subscriptionStatus.is_active ? 'hover:bg-red-50' : 'opacity-50 cursor-not-allowed'
                          }`}
                          style={{ borderColor: '#ef4444', color: '#ef4444' }}
                        >
                          <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>

            {/* EMPTY STATE - PURE WHITE VERSION */}
            {products.length === 0 && !isLoading && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="col-span-full px-4 py-12 sm:py-16"
              >
                <div className="max-w-3xl mx-auto">
                  <div className="text-center mb-8 sm:mb-12">
                    <motion.div
                      animate={{
                        scale: [1, 1.1, 1],
                        rotate: [0, 5, -5, 0],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                      className="inline-flex items-center justify-center w-20 h-20 sm:w-28 sm:h-28 rounded-full bg-white border-2 border-gray-200 mb-6 sm:mb-8 shadow-lg"
                    >
                      <Package className="w-10 h-10 sm:w-14 sm:h-14 text-gray-600" />
                    </motion.div>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">Your Store is Ready for Designs!</h2>
                    <p className="text-base sm:text-lg text-gray-600 mb-2">Your fashion brand is live, but your catalog is empty.</p>
                    <p className="text-sm sm:text-base text-gray-500">Add your first design and start showcasing your creativity!</p>
                  </div>

                  <div className="bg-white rounded-2xl sm:rounded-3xl shadow-lg border-2 border-gray-200 p-6 sm:p-8 lg:p-10 mb-8 sm:mb-10">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-6 sm:mb-8 text-center">How to Add Your First Design</h3>

                    <div className="space-y-5 sm:space-y-6">
                      {[
                        { num: 1, title: 'Click "Add Design" Button', desc: 'Open the upload form from the button below or navigation menu' },
                        { num: 2, title: 'Upload Product Photos', desc: 'Add up to 6 high-quality photos of your design' },
                        { num: 3, title: 'Add Product Details', desc: 'Fill in name, price, description, and options' },
                        { num: 4, title: 'Publish to Your Store', desc: 'Your design goes live instantly!' }
                      ].map((step) => (
                        <div key={step.num} className="flex gap-3 sm:gap-4">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg shadow-md" style={{ backgroundColor: COLORS.AUTUMN }}>
                              {step.num}
                            </div>
                          </div>
                          <div className="flex-1 pt-1">
                            <h4 className="font-bold text-gray-800 text-sm sm:text-lg mb-1">{step.title}</h4>
                            <p className="text-xs sm:text-sm text-gray-600">{step.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="text-center mb-8">
                    <Link to="/UploadProduct">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="text-white font-bold px-8 sm:px-10 py-4 sm:py-5 rounded-xl sm:rounded-2xl text-base sm:text-lg shadow-xl inline-flex items-center gap-3 transition-all duration-300"
                        style={{ backgroundColor: COLORS.AUTUMN }}
                      >
                        <Plus className="w-5 h-5 sm:w-6 sm:h-6" />
                        Add Your First Design Now
                      </motion.button>
                    </Link>
                    <p className="text-xs sm:text-sm text-gray-500 mt-3 sm:mt-4">Takes only 2-3 minutes!</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-gray-200">
                    {[
                      { emoji: '🎨', title: 'Showcase Creativity', desc: 'Display your unique designs' },
                      { emoji: '💰', title: 'Start Earning', desc: 'Receive orders directly' },
                      { emoji: '🚀', title: 'Grow Your Brand', desc: 'Build your fashion business' }
                    ].map((benefit, idx) => (
                      <div key={idx} className="text-center">
                        <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">{benefit.emoji}</div>
                        <p className="font-bold text-gray-800 text-sm sm:text-base mb-1">{benefit.title}</p>
                        <p className="text-xs sm:text-sm text-gray-600">{benefit.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        }
      />
    </>
  );
};

export default HomePage;
